import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, Calendar, Users } from "lucide-react";
import api from "@/lib/api";
import { toLocalYMD, todayYMD, plusDaysYMD } from "@/lib/dateUtils";

export default function SearchWidget({ compact = false, initial = {} }) {
    const navigate = useNavigate();
    const today = todayYMD();
    const nextWeek = plusDaysYMD(7);

    const [q, setQ] = useState(initial.q || initial.destination || "");
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selected, setSelected] = useState(null); // { type, id, label }
    const [checkIn, setCheckIn] = useState(initial.check_in || today);
    const [checkOut, setCheckOut] = useState(initial.check_out || nextWeek);
    const [adults, setAdults] = useState(initial.adults || 2);
    const [children, setChildren] = useState(initial.children || 0);
    const [childAges, setChildAges] = useState(() => {
        const parsed = (initial.child_ages || "").toString().split(",").filter(Boolean).map(Number);
        return parsed.length ? parsed : [];
    });
    const [rooms, setRooms] = useState(initial.rooms || 1);
    const [showGuests, setShowGuests] = useState(false);

    // Compute min checkout = checkIn + 1 day
    const minCheckOut = (() => {
        const base = checkIn ? new Date(checkIn) : new Date();
        const d = new Date(base);
        d.setDate(d.getDate() + 1);
        return toLocalYMD(d);
    })();

    // Auto-bump checkout when checkIn moves past it
    useEffect(() => {
        if (checkIn && checkOut && new Date(checkOut) <= new Date(checkIn)) {
            setCheckOut(minCheckOut);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkIn]);

    // Keep childAges array length in sync with children count
    useEffect(() => {
        setChildAges((prev) => {
            const next = [...prev];
            while (next.length < children) next.push(5);
            while (next.length > children) next.pop();
            return next;
        });
    }, [children]);

    useEffect(() => {
        if (!q || q.length < 2) { setSuggestions([]); return; }
        const t = setTimeout(async () => {
            try {
                const [hRes, aRes] = await Promise.all([
                    api.get(`/hotels?q=${encodeURIComponent(q)}&limit=5`),
                    api.get(`/destinations/autocomplete?q=${encodeURIComponent(q)}&limit=8`),
                ]);
                const items = aRes.data.items || [];
                const dSuggestions = items
                    .filter((i) => i.type === "destination")
                    .map((d) => ({
                        type: "destination",
                        id: d.slug,
                        label: d.name,
                        sub: `${d.hotel_count} otel${d.breadcrumb?.length ? " · " + d.breadcrumb.join(" › ") : ""}`,
                        icon: d.level >= 3 ? "📍" : "🏝️",
                    }));
                const poiSuggestions = items
                    .filter((i) => i.type === "poi")
                    .slice(0, 3)
                    .map((p) => ({
                        type: "destination",
                        id: p.destination_slug,
                        label: p.name,
                        sub: p.poi_type === "beach" ? "Plaj" : p.poi_type === "airport" ? "Havalimanı" : p.poi_type === "attraction" ? "Gezi Yeri" : "Marina",
                        icon: p.poi_type === "beach" ? "🏖️" : p.poi_type === "airport" ? "✈️" : p.poi_type === "marina" ? "⚓" : "🏛️",
                    }));
                const hMatches = (hRes.data.items || []).slice(0, 5);
                setSuggestions([
                    ...dSuggestions,
                    ...poiSuggestions,
                    ...hMatches.map((h) => ({ type: "hotel", id: h.id, label: h.name, sub: `${h.destination_name}`, icon: "🏨" })),
                ]);
            } catch { /* ignore */ }
        }, 250);
        return () => clearTimeout(t);
    }, [q]);

    const pickSuggestion = (s) => {
        setSelected(s);
        setQ(s.label);
        setShowSuggestions(false);
    };

    const handleSearch = (e) => {
        e?.preventDefault();
        const params = new URLSearchParams();
        params.set("check_in", checkIn);
        params.set("check_out", checkOut);
        params.set("adults", adults);
        params.set("children", children);
        if (childAges.length) params.set("child_ages", childAges.join(","));
        params.set("rooms", rooms);
        // If user picked a hotel from suggestions, go directly to hotel detail with dates
        if (selected?.type === "hotel") {
            navigate(`/hotel/${selected.id}?${params.toString()}`);
            return;
        }
        if (selected?.type === "destination") {
            params.set("destination", selected.id);
        } else if (q) {
            params.set("q", q);
        }
        navigate(`/search?${params.toString()}`);
    };

    return (
        <form
            onSubmit={handleSearch}
            data-testid="search-widget"
            className={`w-full bg-white dark:bg-slate-900 rounded-2xl shadow-soft border border-slate-200 dark:border-slate-800 p-3 md:p-4 ${compact ? "" : "shadow-xl"}`}
        >
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-stretch">
                <div className="md:col-span-4 relative">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 ml-3">Otel adı veya bölge</label>
                    <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-transparent focus-within:border-sky-500">
                        <MapPin className="h-5 w-5 text-sky-500 flex-shrink-0" />
                        <input
                            data-testid="search-q"
                            type="text"
                            value={q}
                            onChange={(e) => { setQ(e.target.value); setSelected(null); setShowSuggestions(true); }}
                            onFocus={() => setShowSuggestions(true)}
                            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                            placeholder="Antalya, Bodrum, Grand Otel..."
                            className="w-full bg-transparent outline-none font-medium text-slate-900 dark:text-white"
                        />
                    </div>
                    {showSuggestions && suggestions.length > 0 && (
                        <div data-testid="search-suggestions" className="absolute z-50 left-0 right-0 mt-2 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xl overflow-hidden">
                            {suggestions.map((s) => (
                                <button
                                    key={`${s.type}-${s.id}`}
                                    type="button"
                                    onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
                                    className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800"
                                >
                                    <span className="text-lg">{s.icon}</span>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-slate-900 dark:text-white truncate">{s.label}</div>
                                        <div className="text-xs text-slate-500">{s.sub}</div>
                                    </div>
                                    <span className="text-[10px] uppercase font-bold text-sky-500">{s.type === "hotel" ? "Otel" : "Bölge"}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="md:col-span-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 ml-3">Giriş</label>
                    <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-transparent focus-within:border-sky-500">
                        <Calendar className="h-5 w-5 text-sky-500 flex-shrink-0" />
                        <input data-testid="search-checkin" type="date" value={checkIn} min={today} onChange={(e) => setCheckIn(e.target.value)} className="w-full bg-transparent outline-none font-medium text-slate-900 dark:text-white" />
                    </div>
                </div>

                <div className="md:col-span-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 ml-3">Çıkış</label>
                    <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-transparent focus-within:border-sky-500">
                        <Calendar className="h-5 w-5 text-sky-500 flex-shrink-0" />
                        <input data-testid="search-checkout" type="date" value={checkOut} min={minCheckOut} onChange={(e) => setCheckOut(e.target.value)} className="w-full bg-transparent outline-none font-medium text-slate-900 dark:text-white" />
                    </div>
                </div>

                <div className="md:col-span-2 relative">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 ml-3">Misafir</label>
                    <button type="button" data-testid="search-guests-trigger" onClick={() => setShowGuests((v) => !v)} className="w-full flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-transparent hover:border-sky-500 text-left">
                        <Users className="h-5 w-5 text-sky-500 flex-shrink-0" />
                        <span className="font-medium text-slate-900 dark:text-white truncate">{adults} Yet · {children} Çoc · {rooms} Oda</span>
                    </button>
                    {showGuests && (
                        <div data-testid="search-guests-popover" className="absolute z-50 left-0 right-0 md:right-auto md:w-72 mt-2 p-4 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xl space-y-3">
                            <Stepper label="Yetişkin (12+ yaş)" value={adults} setValue={setAdults} min={1} max={9} testid="adults" />
                            <Stepper label="Çocuk (0-12 yaş)" value={children} setValue={setChildren} min={0} max={6} testid="children" />
                            {children > 0 && (
                                <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Çocukların yaşı</div>
                                    <div className="grid grid-cols-3 gap-2">
                                        {childAges.map((age, idx) => (
                                            <div key={idx} className="flex flex-col items-center gap-1">
                                                <span className="text-[10px] font-semibold text-slate-500">{idx + 1}. çocuk</span>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="17"
                                                    value={age}
                                                    onChange={(e) => {
                                                        const v = Math.min(17, Math.max(0, Number(e.target.value)));
                                                        setChildAges((prev) => prev.map((a, i) => i === idx ? v : a));
                                                    }}
                                                    data-testid={`child-age-${idx}`}
                                                    className="w-full px-2 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-center text-sm font-bold text-slate-900 dark:text-white"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <Stepper label="Oda" value={rooms} setValue={setRooms} min={1} max={5} testid="rooms" />
                            <button type="button" onClick={() => setShowGuests(false)} className="w-full mt-2 py-2 rounded-xl bg-sky-500 text-white font-semibold">Tamam</button>
                        </div>
                    )}
                </div>

                <div className="md:col-span-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-transparent ml-3">.</label>
                    <button data-testid="search-submit" type="submit" className="w-full h-full px-4 py-3 rounded-xl bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 text-white font-semibold flex items-center justify-center gap-2 shadow-glow transition-all">
                        <Search className="h-5 w-5" /> Ara
                    </button>
                </div>
            </div>
        </form>
    );
}

function Stepper({ label, value, setValue, min, max, testid }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{label}</span>
            <div className="flex items-center gap-3">
                <button type="button" data-testid={`stepper-${testid}-minus`} onClick={() => setValue(Math.max(min, value - 1))} className="h-7 w-7 rounded-full border border-slate-300 dark:border-slate-600 flex items-center justify-center text-slate-900 dark:text-white disabled:opacity-30" disabled={value <= min}>−</button>
                <span data-testid={`stepper-${testid}-value`} className="w-5 text-center font-semibold text-slate-900 dark:text-white">{value}</span>
                <button type="button" data-testid={`stepper-${testid}-plus`} onClick={() => setValue(Math.min(max, value + 1))} className="h-7 w-7 rounded-full border border-slate-300 dark:border-slate-600 flex items-center justify-center text-slate-900 dark:text-white disabled:opacity-30" disabled={value >= max}>+</button>
            </div>
        </div>
    );
}
